import { zodResolver } from "@hookform/resolvers/zod";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { Announcement03 } from "@untitledui/icons";
import { useForm } from "react-hook-form";
import z from "zod/v3";
import { useCreateSmartCollabGroup } from "@/api/smart-collab-groups";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";
import { success, error as toastError } from "@/providers/ToastProvider";
import type { StepperStep } from "./collab-stepper-modal";
import { CollabStepperModal } from "./collab-stepper-modal";
import type { AutomationsFormState } from "./steps/AutomationStep5";
import { AutomationStep5, automationEntrySchema, INITIAL_AUTOMATIONS } from "./steps/AutomationStep5";
import { DetailsStep1 } from "./steps/DetailsStep1";
import { InvitationStep4 } from "./steps/InvitationStep4";
import { ProductStep2 } from "./steps/ProductStep2";
import { ReviewStep6 } from "./steps/ReviewStep6";
import { SamplesStep3 } from "./steps/SamplesStep3";

export type ProductEntry = {
  id: string;
  name: string;
  thumbnail: string | null;
  price: string;
  commissionRate: string;
  shopAdsEnabled: boolean;
  shopAdsCommission: string;
};

export type InvitedCreator = {
  id: string;
  name: string;
  handle: string;
  avatar: string | null;
  initials: string;
  cohortId: string | null;
  // A cohort name to create (from a CSV row whose cohort didn't match an existing one) — mutually
  // exclusive with cohortId, only ever set for CSV-imported rows.
  pendingCohortName: string | null;
  isCreator: boolean;
  previouslyInvited: boolean;
  hasActiveCollaboration: boolean;
};

export type CreateCollabFormValues = {
  collaborationName: string;
  activeUntil: string;
  products: ProductEntry[];
  invitedCreatorIds: string[];
  invitedCreators: InvitedCreator[];
  offerFreeSamples: boolean;
  approvalRule: "auto-approve" | "manually-review" | null;
  minGmv: string;
  minFollowers: string;
  minPostRate: string;
  minAvgViews: string;
  categoryFit: string;
  sellerEmail: string;
  invitationMessage: string;
  whatsappNumber: string;
  automations: AutomationsFormState;
};

export const MAX_INVITATION_MESSAGE_LENGTH = 500;

const productEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  thumbnail: z.string().nullable(),
  price: z.string(),
  // Same rule as create-target-collaboration-modal.tsx's validateStep: reject missing or <= 0
  // commission rates instead of letting Number("") coerce to a silent 0%.
  commissionRate: z.string().refine((v) => Number(v) > 0, "Commission rate is required"),
  shopAdsEnabled: z.boolean(),
  shopAdsCommission: z.string(),
});

const invitedCreatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  handle: z.string(),
  avatar: z.string().nullable(),
  initials: z.string(),
  cohortId: z.string().nullable(),
  pendingCohortName: z.string().nullable(),
  isCreator: z.boolean(),
  previouslyInvited: z.boolean(),
  hasActiveCollaboration: z.boolean(),
});

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const collabFormSchema = z.object({
  collaborationName: z.string().min(1, "Required"),
  // Mirrors DatePickerInput's minValue in DetailsStep1.tsx (today+1) - re-checked here so a
  // manually-edited value can't slip past the picker's restriction.
  activeUntil: z
    .string()
    .min(1, "Required")
    .refine((v) => {
      if (!v) return true;
      return parseDate(v).compare(today(getLocalTimeZone()).add({ days: 1 })) >= 0;
    }, "Active Until must be tomorrow or later"),
  products: z.array(productEntrySchema).min(1, "Add at least one product"),
  invitedCreatorIds: z.array(z.string()).min(1, "Select at least one creator"),
  invitedCreators: z.array(invitedCreatorSchema),
  offerFreeSamples: z.boolean(),
  approvalRule: z.union([z.literal("auto-approve"), z.literal("manually-review"), z.null()]),
  minGmv: z.string(),
  minFollowers: z.string(),
  minPostRate: z.string(),
  minAvgViews: z.string(),
  categoryFit: z.string(),
  // Same requirement + validation as create-target-collaboration-modal.tsx's sellerEmail.
  sellerEmail: z.string().min(1, "Email address is required").regex(EMAIL_REGEX, "Please enter a valid email address"),
  invitationMessage: z
    .string()
    .max(MAX_INVITATION_MESSAGE_LENGTH, `Message must be ${MAX_INVITATION_MESSAGE_LENGTH} characters or less`),
  // Generic - the number can be from any country, so just require it to be all digits and
  // longer than a bare local extension. Optional: empty is valid, only validated when filled in.
  whatsappNumber: z
    .string()
    .refine((v) => v.trim() === "" || (/^\d+$/.test(v.trim()) && v.trim().length > 5), "The number format is invalid"),
  automations: z.object({
    collaboration_invite: automationEntrySchema,
    sample_delivered: automationEntrySchema,
    creator_dormant: automationEntrySchema,
    first_collab_video: automationEntrySchema,
  }),
});

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const CreateCollabModal = ({ open, onOpenChange }: Props) => {
  const createMutation = useCreateSmartCollabGroup();
  const triggerTypes = useCollabTriggerTypes();
  const form = useForm<CreateCollabFormValues>({
    resolver: zodResolver(collabFormSchema),
    defaultValues: {
      collaborationName: "",
      activeUntil: "",
      invitedCreatorIds: [],
      invitedCreators: [],
      products: [],
      offerFreeSamples: true,
      approvalRule: "auto-approve",
      minGmv: "",
      minFollowers: "",
      minPostRate: "",
      minAvgViews: "",
      categoryFit: "",
      sellerEmail: "",
      invitationMessage: "",
      whatsappNumber: "",
      automations: INITIAL_AUTOMATIONS,
    },
  });

  const steps: StepperStep<CreateCollabFormValues>[] = [
    {
      id: "details",
      label: "Details",
      render: () => <DetailsStep1 />,
      fields: ["collaborationName", "activeUntil"],
    },
    { id: "product", label: "Product", render: () => <ProductStep2 />, fields: ["products"] },
    { id: "samples", label: "Samples", render: () => <SamplesStep3 /> },
    {
      id: "invitation",
      label: "Invitation",
      render: () => <InvitationStep4 />,
      fields: ["invitedCreatorIds", "sellerEmail", "invitationMessage", "whatsappNumber"],
    },
    { id: "automation", label: "Automation", render: () => <AutomationStep5 /> },
    { id: "review", label: "Review", render: () => <ReviewStep6 /> },
  ];

  const onSubmit = async (values: CreateCollabFormValues) => {
    const hasMinRequirements =
      values.offerFreeSamples &&
      values.approvalRule === "auto-approve" &&
      (values.minGmv !== "" ||
        values.minFollowers !== "" ||
        values.minPostRate !== "" ||
        values.minAvgViews !== "" ||
        values.categoryFit !== "");

    try {
      await createMutation.mutateAsync({
        name: values.collaborationName,
        activeUntil: new Date(values.activeUntil).toISOString(),
        products: values.products.map((p) => ({
          productId: p.id,
          commissionRate: Number(p.commissionRate),
          shopAdsCommissionRate: p.shopAdsEnabled && p.shopAdsCommission ? Number(p.shopAdsCommission) : null,
        })),
        creatorOpenIds: values.invitedCreatorIds,
        creatorCohorts: values.invitedCreators
          .filter((c) => c.cohortId || c.pendingCohortName)
          .map((c) => ({ tiktokCreatorOpenId: c.id, cohortId: c.cohortId, cohortName: c.pendingCohortName })),
        hasFreeSample: values.offerFreeSamples,
        isSampleApprovalExempt:
          values.offerFreeSamples && values.approvalRule === "auto-approve" && !hasMinRequirements,
        minRequirements: hasMinRequirements
          ? {
              ...(values.minGmv !== "" && { minGmv: Number(values.minGmv) }),
              ...(values.minFollowers !== "" && { minFollowers: Number(values.minFollowers) }),
              ...(values.minPostRate !== "" && { minPostRate: Number(values.minPostRate) }),
              ...(values.minAvgViews !== "" && { minAvgViews: Number(values.minAvgViews) }),
              ...(values.categoryFit !== "" && { categoryFit: values.categoryFit }),
            }
          : undefined,
        sellerEmail: values.sellerEmail.trim(),
        ...(values.invitationMessage.trim() && { invitationMessage: values.invitationMessage.trim() }),
        ...(values.whatsappNumber.trim() && { whatsappNumber: values.whatsappNumber.trim() }),
        automations: Object.fromEntries(
          triggerTypes.map((triggerType) => [triggerType, values.automations[triggerType]]),
        ),
      });

      success("Smart Collab Group created");
      onOpenChange(false);
      form.reset();
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Failed to create Smart Collab Group");
    }
  };

  return (
    <CollabStepperModal
      open={open}
      onOpenChange={onOpenChange}
      title="New Smart Collab Group"
      subtitle="Group TikTok collaborations under one campaign"
      icon={Announcement03}
      form={form}
      steps={steps}
      onSubmit={onSubmit}
      submitLabel="Create"
      submitPendingLabel="Creating…"
      isSubmitting={createMutation.isPending}
    />
  );
};
